/**
 * FusionMsiMarketingNav.jsx — shared marketing header.
 * Shaped by: md3c step 4b; md3c sibling look-and-feel.
 * Invariants: wordmark, links, and home href come from the brand config.
 * /splashpage still shows the CareStart wordmark on non-md3c hosts.
 * The flash14 star mark is unchanged, including its hard-coded colors.
 * Tagline/byline stack under the wordmark so the nav stays a single sibling row.
 * Related: resolveMarketingBrandConfig.js, FusionMsiMarketingLayout.jsx,
 * MarketingColorModeToggle.jsx, fusionAperture/lab/variants/flash14/StarMark.jsx.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Link, Stack, Typography } from '@mui/material';
import { isMd3cMarketingHost } from 'features/md3c/marketing/md3cMarketingRouting';
import { resolveMarketingBrandConfig } from '../marketing/resolveMarketingBrandConfig';
import {
  FUSION_MSI_INTRO_DIM_OPACITY,
  useFusionMsiIntro,
} from './FusionMsiIntroContext';
import {
  CARESTART_SPLASH_WORDMARK,
  isCareStartSplashPath,
} from '../marketing/careStartSplashCopy';
import Flash14StarMark from './fusionAperture/lab/variants/flash14/StarMark';
import MarketingColorModeToggle from './MarketingColorModeToggle';

function FusionMsiMarketingNav({ compact }) {
  const { pathname } = useLocation();
  const { introPhase } = useFusionMsiIntro();
  const introActive = introPhase === 'intro' || introPhase === 'handoff';
  const brand = resolveMarketingBrandConfig();
  const wordmark =
    isCareStartSplashPath(pathname) && !isMd3cMarketingHost()
      ? CARESTART_SPLASH_WORDMARK
      : brand.wordmark;
  const hasBrandStack = Boolean(brand.tagline || brand.byline);

  return (
    <Box
      component="header"
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: hasBrandStack ? 'flex-start' : 'center',
        gap: 2,
        pl: { xs: 2, sm: 4 },
        py: 1,
        pr: { xs: 2, sm: 4 },
        borderBottom: 1,
        borderColor: 'divider',
        flexWrap: 'wrap',
        opacity: introActive ? FUSION_MSI_INTRO_DIM_OPACITY : 1,
        transition: 'opacity 700ms ease-out',
        pointerEvents: introActive ? 'none' : 'auto',
      }}
    >
      <Link
        component={RouterLink}
        to={brand.homeHref}
        underline="none"
        aria-label={`${wordmark} home`}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mr: 2, minWidth: 0 }}
      >
        <Box
          aria-hidden
          sx={{
            height: compact ? 26 : 30,
            width: compact ? 23 : 27,
            flexShrink: 0,
            mt: hasBrandStack ? 0.25 : 0,
          }}
        >
          <Flash14StarMark shimmer decorative heroSlot />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="span"
            variant="h6"
            sx={{
              fontWeight: 700,
              letterSpacing: '0.01em',
              color: 'text.primary',
              whiteSpace: 'nowrap',
              lineHeight: 1.15,
              display: 'block',
            }}
          >
            {wordmark}
          </Typography>
          {brand.tagline ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1.3, mt: 0.25 }}
            >
              {brand.tagline}
            </Typography>
          ) : null}
          {brand.byline ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1.3 }}
            >
              {brand.byline}
            </Typography>
          ) : null}
        </Box>
      </Link>

      <Stack
        direction="row"
        spacing={compact ? 1.5 : 2.5}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ ml: 'auto' }}
      >
        {(brand.navItems || []).map((item) => {
          const active =
            !item.external &&
            (pathname === item.href ||
              (item.activePrefix === true && pathname.startsWith(`${item.href}/`)));
          if (item.external) {
            return (
              <Link
                key={item.label}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                color="text.secondary"
                variant="body2"
              >
                {item.label}
              </Link>
            );
          }
          return (
            <Link
              key={item.label}
              component={RouterLink}
              to={item.href}
              underline={active ? 'always' : 'hover'}
              color={active ? 'primary' : 'text.secondary'}
              variant="body2"
              sx={{ fontWeight: active ? 600 : 400 }}
            >
              {item.label}
            </Link>
          );
        })}
        <MarketingColorModeToggle />
      </Stack>
    </Box>
  );
}

FusionMsiMarketingNav.propTypes = {
  compact: PropTypes.bool,
};

export default FusionMsiMarketingNav;
